import { useState } from 'react';
import { Link } from 'wouter';
import { api, type Lifeline as LifelineData, type LifelineRow } from '../lib/api';
import { useResource } from '../lib/use-resource';
import { trackPct, trackMarks, type TrackSpec } from '../lib/track';
import { dayLocal, usd } from '../lib/time';
import './lifeline.css';

/**
 * Every new customer's lifecycle, from sign-up to paying, staying or leaving.
 *
 * Every sign-up in the period, newest first (fix-331, ADR-0356): the ones
 * still in trial with their next move — a link to the card that does
 * something about it — and the ones that paid or closed with what they paid.
 * The tiles are the period's cohort: sign-ups, converted (any payment), a
 * second payment (most likely staying), the rate and the revenue. The periods,
 * the window, the marks and each row's next step come from the desk (fix-78):
 * the lifecycle rows and settings decide them, and nothing here holds a day.
 */
export function Lifeline() {
  // null: the desk's own default (settings.lifeline_period_days).
  const [period, setPeriod] = useState<number | null>(null);
  const r = useResource<LifelineData>(() => api.lifeline(period ?? undefined), { pollMs: 60_000, deps: [period] });

  if (r.loading && !r.data) return <Board><p className="list-empty">Reading the trials…</p></Board>;
  if (!r.data) return <Board><p className="list-empty list-error">{r.error?.message}</p></Board>;

  const d = r.data;
  const spec: TrackSpec = { ...d.track, window_days: d.window_days };
  return (
    <Board>
      <div className="lifeline-head">
        <div>
          <div className="mono eyebrow">Lifeline · everyone who signed up in the last {d.period_days} days</div>
          <h1>New customers, sign-up to paying</h1>
          <p className="lede">
            Every customer who converted had placed a call. Most who leave never write in.
            One payment is off the trial; a second means they are most likely staying.
          </p>
          <div className="lifeline-period mono" role="group" aria-label="Sign-up period">
            {d.periods.map((p) => (
              <button key={p} type="button" aria-pressed={p === d.period_days} onClick={() => setPeriod(p)}>{p} days</button>
            ))}
          </div>
        </div>
        <div className="tiles">
          <Tile n={d.sign_ups} label="sign-ups" />
          <Tile n={d.converted} label="converted" tone="teal"
            sub={d.conversion_rate === null ? 'no sign-ups' : `${Math.round(d.conversion_rate * 100)}% of sign-ups`} />
          <Tile n={d.second_payment} label="second payment" tone="teal" />
          <Tile n={usd(d.revenue)} label="paid · inc GST" tone="teal" />
          <Tile n={d.in_trial} label="in trial" />
          <Tile n={d.stuck} label={`no call by day ${d.track.rescue_from_day}+`} tone="rust" />
          <Tile n={d.closures_due} label="closure card next" />
        </div>
      </div>

      <div className="track-head">
        <div className="mono eyebrow">Customer</div>
        <div className="track-scale">
          <div className="track-rule" />
          <div className="track-labels mono">
            {trackMarks(spec).map((m) => (
              <span key={m.day} className={`is-${m.kind || 'plain'}`} style={{ left: `${trackPct(m.day, spec)}%` }}>{m.label}</span>
            ))}
          </div>
        </div>
        <div className="mono eyebrow">Next move</div>
      </div>

      <div className="rows">
        {d.rows.length === 0 && <p className="list-empty">No sign-ups in the last {d.period_days} days.</p>}
        {d.rows.map((row) => <Row key={row.account_no} row={row} spec={spec} />)}
      </div>

      <div className="legend mono">
        <Key className="key-signup" label="signed up" />
        <Key className="key-call" label="first call" />
        <Key className="key-reach" label="we reached out" />
        <Key className="key-ext" label="extension offered" />
        <Key className="key-grant" label="extension granted" />
        <Key className="key-closure" label="closure sent" />
        <Key className="key-paid" label="paid" />
        <span className="legend-note">newest sign-up first · teal paid · faded closed</span>
      </div>
    </Board>
  );
}

function Row({ row, spec }: { row: LifelineRow; spec: TrackSpec }) {
  const at = (stamp: string | null) => trackPct(daysBetween(row.started_at, stamp), spec);
  const today = trackPct(row.day ?? 0, spec);
  const tone = row.closed ? 'is-closed' : row.stuck ? 'is-stuck' : row.paid ? 'is-converted' : '';

  return (
    <div className={`row ${tone}`}>
      <div className="row-who">
        <div className="row-name">{row.name ?? row.account_no}</div>
        <div className="mono row-sub">
          {row.paid
            ? `${row.closed ? `${row.status} · had paid` : 'Converted · paid'} ${usd(row.paid_total)} · signed up ${dayLocal(row.started_at)}`
            : `${row.status ?? 'unknown'} · signed up ${dayLocal(row.started_at)}`}
        </div>
      </div>

      <div className="row-track">
        <div
          className="row-line"
          style={{ width: `${today}%`, background: row.closed ? 'var(--line)' : row.stuck ? 'var(--rust)' : row.paid ? 'var(--teal)' : row.extended_until ? 'var(--amber)' : 'var(--line)' }}
        />
        <Dot pct={0} className="key-signup" title="Signed up" />
        {row.reached_out_at && <Dot pct={at(row.reached_out_at)} className="key-reach" title={`We reached out ${dayLocal(row.reached_out_at)}`} />}
        {row.extension_offered_at && <Dot pct={at(row.extension_offered_at)} className="key-ext" title={`Extension offered ${dayLocal(row.extension_offered_at)}`} />}
        {row.extension_granted_at && <Dot pct={at(row.extension_granted_at)} className="key-grant" title={`Extension granted ${dayLocal(row.extension_granted_at)}, until ${dayLocal(row.extended_until)}`} />}
        {row.closure_sent_at && <Dot pct={at(row.closure_sent_at)} className="key-closure" title={`Closure sent ${dayLocal(row.closure_sent_at)}`} />}
        {row.first_call_at && <Dot pct={at(row.first_call_at)} className="key-call" title={`First call ${dayLocal(row.first_call_at)}`} />}
        {row.converted_at && <Dot pct={at(row.converted_at)} className="key-paid" title={`Paid ${dayLocal(row.converted_at)}`} />}
        {row.second_paid_at && <Dot pct={at(row.second_paid_at)} className="key-paid" title={`Second payment ${dayLocal(row.second_paid_at)}`} />}
        <Dot pct={today} className={`key-today ${row.stuck ? 'today-stuck' : row.paid ? 'today-paid' : ''}`} title="Today" />
        <span
          className={`mono row-caption ${row.stuck ? 'is-rust' : row.paid ? 'is-teal' : ''} ${today > 60 ? 'is-flip' : ''}`}
          style={{ left: `${today}%` }}
        >
          {caption(row)}
        </span>
      </div>

      <div className="row-move">
        {row.next_move_card_id ? (
          <Link href={`/cards/${row.next_move_card_id}`} className="row-move-link">{row.next_move}</Link>
        ) : (
          <span className="mono row-move-quiet">{moveText(row)}</span>
        )}
      </div>
    </div>
  );
}

/** The next step with its date in the operator's day; the desk's sentence when there is none. */
function moveText(row: LifelineRow): string {
  const n = row.next_due;
  if (!n || !row.in_trial) return row.next_move;
  const what = `T${n.template_id} ${n.title}`;
  return n.in_days === 0 ? `${what} — due now` : `Next: ${what} · ${dayLocal(n.due_at)}`;
}

function caption(row: LifelineRow): string {
  if (row.paid) {
    const n = row.payments_total;
    return `paid day ${daysBetween(row.started_at, row.converted_at)} · ${n} payment${n === 1 ? '' : 's'}`;
  }
  if (row.closed) return `${row.status} day ${row.day} · ${row.called ? 'called' : 'never called'}`;
  if (row.stuck) {
    return row.reached_out_at
      ? `reached out day ${daysBetween(row.started_at, row.reached_out_at)} · no call`
      : 'no call yet';
  }
  if (row.called) return `called day ${daysBetween(row.started_at, row.first_call_at)} · ${row.calls_total} calls`;
  return `day ${row.day}`;
}

/** Whole days between two desk timestamps. Both are UTC; see lib/time.ts. */
function daysBetween(from: string | null, to: string | null): number {
  if (!from || !to) return 0;
  const a = Date.parse(`${from.replace(' ', 'T')}Z`);
  const b = Date.parse(`${to.replace(' ', 'T')}Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, Math.floor((b - a) / 86400000));
}

const Dot = ({ pct, className, title }: { pct: number; className: string; title: string }) => (
  <span className={`track-dot ${className}`} style={{ left: `${pct}%` }} title={title} />
);

const Key = ({ className, label }: { className: string; label: string }) => (
  <span className="legend-key"><span className={`track-dot ${className}`} />{label}</span>
);

const Tile = ({ n, label, tone, sub }: { n: number | string; label: string; tone?: string; sub?: string }) => (
  <div className={`tile ${tone ? `tile-${tone}` : ''}`}>
    <div className="tile-n">{n}</div>
    <div className="tile-label">{label}</div>
    {sub && <div className="tile-sub">{sub}</div>}
  </div>
);

const Board = ({ children }: { children: React.ReactNode }) => (
  <main className="board">{children}</main>
);
